import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AjaxService } from '../../../service/ajax.service';
import { UtilService } from '../../../service/util.service';
import { API } from '../../../../environments/environment';
import { UserService } from 'src/app/services/user.service';

interface Assessor {
  id: string;
  nome: string;
  email?: string;
}

interface Cliente {
  id: string;
  nome: string;
  email?: string;
}

interface TipoAtendimento {
  id: number;
  nome: string;
}



@Component({
  selector: 'app-agendar-atendimento',
  templateUrl: './agendar-atendimento.component.html',
  styleUrls: ['./agendar-atendimento.component.scss']
})
export class AgendarAtendimentoComponent implements OnInit {

  loading = false;
  private assessorIdParametro: string = '';
  private pendingRequests = 0;

  formData = {
    assessorId: '' as string,
    clienteId: '' as string,
    tipoId: 2 as number,
    dataInicio: '',
    dataFim: '',
    criarSala: false,
    dataExpiracao: '',
    enviarParaCliente: false,
    observacao: '',
    presencialOnline: 'O' as 'O' | 'P'
  };

  assessores: Assessor[] = [];
  clientes: Cliente[] = [];
  clientesFiltrados: Cliente[] = [];
  clienteBusca = '';
  mostrarListaClientes = false;
  tipos: TipoAtendimento[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ajaxService: AjaxService,
    private util: UtilService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.preencherComParametroDaAgenda();
    this.carregarDados();
  }

  carregarDados(): void {
    this.carregarAssessores();
    this.carregarClientes();
    this.carregarTiposAtendimento();
  }

  preencherComParametroDaAgenda(): void {
    this.route.queryParams.subscribe((params) => {
      const assessorParam =
        params['assessorSelecionado'] ||
        params['assessorId'] ||
        params['idAssessor'] ||
        params['idMedico'];

      if (assessorParam) {
        this.assessorIdParametro = String(assessorParam).trim();
        this.formData.assessorId = this.assessorIdParametro;
      }

      if (params['dataInicio']) this.formData.dataInicio = this.normalizarDataParaInput(params['dataInicio']);
      if (params['dataFim']) this.formData.dataFim = this.normalizarDataParaInput(params['dataFim']);
      if (params['presencialOnline']) {
        this.formData.presencialOnline = params['presencialOnline'] === 'P' ? 'P' : 'O';
      }
      if (this.formData.dataFim && !this.formData.dataExpiracao) {
        this.formData.dataExpiracao = this.formData.dataFim;
      }
    });
  }

  carregarAssessores(): void {
    const url = API + 'empresa/AgendaClinica/GetDropDownMedicos';
    this.iniciarLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.finalizarLoading();
        const lista = response?.data && Array.isArray(response.data) ? response.data : [];
        this.assessores = lista
          .map((item: any) => this.util.converterParaLowercase(item))
          .filter((item: any) => item?.id && item?.text)
          .map((item: any) => ({
            id: item.id,
            nome: item.text,
            email: ''
          }));

        this.aplicarAssessorPreSelecionado();
      },
      error: () => {
        this.finalizarLoading();
        this.assessores = [];
        this.util.toasterShowError('Erro', 'Erro ao carregar assessores');
      }
    });
  }

  carregarClientes(): void {
    const url = API + 'empresa/Paciente/ConsultarClienteUsuario';
    this.iniciarLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.finalizarLoading();
        const lista = response?.data && Array.isArray(response.data) ? response.data : [];
        this.clientes = lista
          .map((item: any) => this.util.converterParaLowercase(item))
          .filter((item: any) => item && (item.id || item.id_paciente_clinica))
          .map((item: any) => ({
            id: (item.id || item.id_paciente_clinica || '').toString(),
            nome: item.nome || 'Cliente sem nome',
            email: item.email || ''
          }));

        this.aplicarFiltroClientes();
      },
      error: () => {
        this.finalizarLoading();
        this.clientes = [];
        this.clientesFiltrados = [];
        this.util.toasterShowError('Erro', 'Erro ao carregar clientes');
      }
    });
  }

  onBuscaClienteChange(): void {
    // Mantém compatibilidade com o template atual.
    this.onClienteInputChange();
  }

  onClienteInputChange(): void {
    this.formData.clienteId = '';
    this.mostrarListaClientes = true;
    this.aplicarFiltroClientes();
  }

  private aplicarFiltroClientes(): void {
    const termo = (this.clienteBusca || '').trim().toLowerCase();

    if (!termo) {
      this.clientesFiltrados = [...this.clientes];
      return;
    }

    this.clientesFiltrados = this.clientes.filter((cliente) => {
      const nome = (cliente.nome || '').toLowerCase();
      const email = (cliente.email || '').toLowerCase();
      return nome.includes(termo) || email.includes(termo);
    });
  }

  abrirListaClientes(): void {
    this.mostrarListaClientes = true;
    this.aplicarFiltroClientes();
  }

  fecharListaClientesComDelay(): void {
    // Delay para permitir clique no item da lista.
    setTimeout(() => {
      this.mostrarListaClientes = false;
    }, 150);
  }

  selecionarCliente(cliente: Cliente): void {
    this.formData.clienteId = cliente.id;
    this.clienteBusca = this.formatarCliente(cliente);
    this.mostrarListaClientes = false;
  }

  private formatarCliente(cliente: Cliente): string {
    return cliente.email ? `${cliente.nome} - ${cliente.email}` : cliente.nome;
  }

  carregarTiposAtendimento(): void {
    const url = API + 'empresa/TipoConsulta/Consultar';
    this.iniciarLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.finalizarLoading();
        const lista = response?.data && Array.isArray(response.data) ? response.data : [];
        this.tipos = lista
          .map((item: any) => this.util.converterParaLowercase(item))
          .filter((item: any) => item && item.id && item.descricao)
          .map((item: any) => ({
            id: Number(item.id),
            nome: item.descricao
          }));

        if (!this.tipos.some(t => t.id === Number(this.formData.tipoId)) && this.tipos.length > 0) {
          this.formData.tipoId = this.tipos[0].id;
        }
      },
      error: () => {
        this.finalizarLoading();
        this.tipos = [];
        this.util.toasterShowError('Erro', 'Erro ao carregar tipos de atendimento');
      }
    });
  }

  agendarAtendimento(): void {
    if (!this.validarFormulario()) return;

    const payload = {
      idMedico: this.formData.assessorId,
      idPaciente: this.formData.clienteId,
      idTipoConsulta: Number(this.formData.tipoId) || 2,
      idUsuarioMarcou: this.getIdUsuarioLogado(),
      dataInicio: this.formData.dataInicio,
      dataFim: this.formData.dataFim,
      dataFimSala: this.formData.dataExpiracao || this.formData.dataFim,
      observacoes: this.formData.observacao || '',
      criarSala: this.formData.criarSala,
      enviarCliente: this.formData.enviarParaCliente,
      presencialOnline: this.formData.presencialOnline
    };

    this.iniciarLoading();

    const url = API + 'empresa/AgendaClinica/SalvarAgendamento';
    this.ajaxService.post(url, payload).subscribe({
      next: (response) => {
        this.finalizarLoading();

        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível agendar');
          return;
        }

        this.util.toasterShowSuccess('Sucesso', 'Atendimento agendado com sucesso!');
        this.limparFormulario();
        this.router.navigate(['/agendar-horarios']);
      },
      error: () => {
        this.finalizarLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar agendamento');
      }
    });
  }

  private normalizarDataParaInput(data: string): string {
    if (!data) return '';
    // datetime-local espera yyyy-MM-ddTHH:mm
    const normalizada = data.replace('Z', '');
    return normalizada.length >= 16 ? normalizada.substring(0, 16) : normalizada;
  }

  validarFormulario(): boolean {
    if (!this.formData.assessorId) {
      this.util.toasterShowError('Validação', 'Selecione um assessor.');
      return false;
    }
    if (!this.formData.clienteId) {
      this.util.toasterShowError('Validação', 'Selecione um cliente.');
      return false;
    }
    if (!this.formData.tipoId) {
      this.util.toasterShowError('Validação', 'Selecione um tipo.');
      return false;
    }
    if (!this.formData.dataInicio) {
      this.util.toasterShowError('Validação', 'Informe a data de início.');
      return false;
    }
    if (!this.formData.dataFim) {
      this.util.toasterShowError('Validação', 'Informe a data de fim.');
      return false;
    }
    if (!this.formData.dataExpiracao) {
      this.util.toasterShowError('Validação', 'Informe a data de expiração.');
      return false;
    }

    const dataInicio = new Date(this.formData.dataInicio);
    const dataFim = new Date(this.formData.dataFim);

    if (dataInicio >= dataFim) {
      this.util.toasterShowError('Validação', 'A data de início deve ser anterior à data de fim.');
      return false;
    }

    return true;
  }

  limparFormulario(): void {
    this.clienteBusca = '';
    this.mostrarListaClientes = false;
    this.aplicarFiltroClientes();
    this.formData = {
      assessorId: this.formData.assessorId || '',
      clienteId: '',
      tipoId: 2,
      dataInicio: '',
      dataFim: '',
      criarSala: false,
      dataExpiracao: '',
      enviarParaCliente: false,
      observacao: '',
      presencialOnline: 'O'
    };
  }

  cadastroRapido(): void {
    this.router.navigate(['/cadastro-cliente']);
  }

  private getIdUsuarioLogado(): number | null {
    const userData = this.userService.getUserData();
    if (!userData) return null;

    const userId =
      Number(userData.User?.Id) ||
      Number((userData as any)?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid']) ||
      0;

    return userId > 0 ? userId : null;
  }

  private aplicarAssessorPreSelecionado(): void {
    if (!this.assessores?.length) return;

    const assessorAtual = (this.formData.assessorId || '').toString().trim();
    if (assessorAtual) {
      const existeAssessorAtual = this.assessores.some(a => a.id.toString().trim() === assessorAtual);
      if (existeAssessorAtual) return;
    }

    if (this.assessorIdParametro) {
      const assessorByParam = this.assessores.find(a => a.id.toString().trim() === this.assessorIdParametro);
      if (assessorByParam) {
        this.formData.assessorId = assessorByParam.id;
        return;
      }
    }

    // fallback para não deixar o select vazio
    this.formData.assessorId = this.assessores[0].id;
  }

  private iniciarLoading(): void {
    this.pendingRequests++;
    if (this.pendingRequests === 1) {
      this.loading = true;
      this.util.showLoading();
    }
  }

  private finalizarLoading(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    if (this.pendingRequests === 0) {
      this.loading = false;
      this.util.closeLoading();
    }
  }
}